/*
 * The addresses of an article, spelled in exactly one place.
 *
 * An article's address is a PAIR: kb.Article.Slug is unique only within a
 * product (UQ_Article_ProductId_Slug), so a slug on its own identifies nothing
 * and every URL that reaches an article carries both segments:
 *
 *     /kb/<productSlug>/<slug>                                  the reader page
 *     /api/public/articles/<productSlug>/<slug>                  the JSON
 *     /api/public/articles/<productSlug>/<slug>/images/<file>    the bytes
 *
 * Why a module for three template literals
 * ----------------------------------------
 * Because the same three shapes used to be written out by hand in a dozen
 * places - the browse tiles, the related list, the permalink redirect, the admin
 * "view" link, the markdown renderer, the image src, the impact report - and
 * every one of them was a one-segment address. Adding a segment to a literal is
 * invisible work: the type checker has nothing to say about a template string,
 * so a missed call site does not fail the build, it fails in a reader's browser
 * as a 404. Routing every address through a function with a required
 * `productSlug` parameter turns exactly that mistake into a compile error, which
 * is the only reason this file is worth the import.
 *
 * The rejected alternative was a single `articleUrl(kind, ...)` with the kind as
 * a discriminator. It reads worse at every call site and buys nothing: these are
 * three different addresses of one thing, not three cases of one address.
 *
 * Note the `/api` prefix on the two API paths. They are WHOLE paths, as the
 * browser's address bar and the server's router spell them - which is what an
 * <img src> and a test asserting a URL both need. The web client's `request`
 * helper prepends `/api` itself and therefore takes the API-relative form; it
 * must not be handed one of these.
 *
 * Every segment is percent-encoded. A slug that survived CK_Article_Slug comes
 * through untouched (the character class is a subset of the unreserved set), so
 * this is purely about what happens when something that is NOT a stored slug
 * reaches here - a hand-typed route parameter, a file name with a space.
 */
/** The reader-facing prefix. The one place `/kb` is written. */
const ARTICLE_PATH_PREFIX = '/kb';
/** The public API prefix. The one place `/api/public/articles` is written. */
const PUBLIC_ARTICLE_API_PREFIX = '/api/public/articles';
/**
 * The page a reader visits, optionally at a heading.
 *
 * The fragment is NOT encoded, unlike the segments. It is produced by
 * `toHeadingId`, which keeps letters and digits of any script, and the browser
 * matches the decoded form against the id stamped on the heading - so encoding
 * it here would break exactly the anchors that need it most.
 */
export function articleHref(productSlug, slug, fragment) {
    const anchor = fragment ? `#${fragment}` : '';
    return `${ARTICLE_PATH_PREFIX}/${segment(productSlug)}/${segment(slug)}${anchor}`;
}
/** The JSON endpoint for one published article. */
export function publicArticlePath(productSlug, slug) {
    return `${PUBLIC_ARTICLE_API_PREFIX}/${segment(productSlug)}/${segment(slug)}`;
}
/**
 * The endpoint serving one image attached to a published article.
 *
 * A string rather than a fetch, because it ends up in an <img src> and the
 * browser does the loading: it gets the ETag, the conditional request and the
 * disk cache for free.
 */
export function publicArticleImagePath(productSlug, slug, fileName) {
    return `${publicArticlePath(productSlug, slug)}/images/${segment(fileName)}`;
}
/**
 * The endpoint listing the articles related to one article.
 *
 * Here rather than at its single call site for the rule this file exists to
 * enforce: `/api/public/articles/` is written once, and adding a segment to the
 * address cannot miss a caller.
 */
export function publicArticleRelatedPath(productSlug, slug) {
    return `${publicArticlePath(productSlug, slug)}/related`;
}
/**
 * One attachment, downloaded THROUGH an article (S47): the address carries the
 * article because the article is what decides whether this reader may have the
 * file. A GET here answers with a redirect to a short-lived SAS URL - what a
 * link the browser follows by itself needs, and enough for anyone who may read
 * the article anonymously. The page fetches publicArticleAttachmentLinkPath
 * instead, so that a signed-in reader's token goes with the request.
 */
export function publicArticleAttachmentPath(productSlug, slug, attachmentId) {
    return `${publicArticlePath(productSlug, slug)}/attachments/${segment(attachmentId.toLowerCase())}`;
}
/** The same download as JSON - an AttachmentDownloadLink - for a fetch that carries a token. */
export function publicArticleAttachmentLinkPath(productSlug, slug, attachmentId) {
    return `${publicArticleAttachmentPath(productSlug, slug, attachmentId)}/link`;
}
/**
 * Parses an href back into the attachment it downloads, or null - how the page
 * recognises, among the links the markdown renderer produced, the ones to
 * intercept. Only the exact shape publicArticleAttachmentPath builds matches.
 */
export function parsePublicArticleAttachmentPath(href) {
    const match = /^\/api\/public\/articles\/([^/?#]+)\/([^/?#]+)\/attachments\/([0-9a-f-]{36})$/i.exec(href);
    if (!match)
        return null;
    return {
        productSlug: decodeURIComponent(match[1]),
        slug: decodeURIComponent(match[2]),
        attachmentId: match[3].toLowerCase(),
    };
}
function segment(value) {
    return encodeURIComponent(value);
}
//# sourceMappingURL=articleUrls.js.map